import { Suspense, useMemo } from 'react';
import { Car, ShieldCheck, UserCheck, Users } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/ui/PageHeader';
import { CardSkeleton } from '@/ui/base/table-skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/base/tabs';

import { CondutoresPage } from '@/modulos/portal-credenciado/condutores/CondutoresPage';
import { EquipePage } from '@/modulos/portal-credenciado/equipe/EquipePage';
import { MonitoresPage } from '@/modulos/portal-credenciado/monitores/MonitoresPage';
import { usePortalAccess } from '@/modulos/portal-credenciado/comum/usePortalAccess';
import { VeiculosPage } from '@/modulos/portal-credenciado/veiculos/VeiculosPage';

type Aba = 'veiculos' | 'condutores' | 'monitores' | 'acessos';

const ABAS_CONHECIDAS: readonly Aba[] = ['veiculos', 'condutores', 'monitores', 'acessos'];

/**
 * `/credenciado/frota-equipe`, hub que reúne em abas as telas que já existem
 * por conta própria (Veículos, Condutores, Monitores e a Minha Equipe). Não
 * tem dado próprio: cada aba é a tela real, só embrulhada aqui para quem
 * prefere um único lugar. Igual a `pages/portal/PortalFrotaEquipe.tsx` do
 * protótipo:
 * - a aba escolhida vive em `?sub=`, lida em minúsculas; aba desconhecida ou
 *   que o perfil não vê volta para "veiculos";
 * - "Condutores" só aparece para credenciado coletivo (PJ ou Cooperativa);
 * - "Acessos & Colaboradores" aparece só para o administrador do credenciado
 *   (`isAdminCred`); papel interno não entra no Portal.
 *
 * Um `h1` só: o desta página. Cada aba recebe `embutida`, e o título da tela
 * embutida sai em `h2`, sem repetir o slot de banners (`PageHeader nivel`).
 */
export function FrotaEquipePage() {
  const { isColetivo, isAdminCred } = usePortalAccess();
  const [params, setParams] = useSearchParams();

  const podeVerAcessos = isAdminCred;

  const abas = useMemo(() => {
    const lista: { valor: Aba; rotulo: string; icone: typeof Car }[] = [
      { valor: 'veiculos', rotulo: 'Veículos', icone: Car },
    ];
    if (isColetivo) {
      lista.push({ valor: 'condutores', rotulo: 'Condutores', icone: UserCheck });
    }
    lista.push({ valor: 'monitores', rotulo: 'Monitores', icone: Users });
    if (podeVerAcessos) {
      lista.push({ valor: 'acessos', rotulo: 'Acessos & Colaboradores', icone: ShieldCheck });
    }
    return lista;
  }, [isColetivo, podeVerAcessos]);

  const subPedida = (params.get('sub') ?? 'veiculos').toLowerCase();
  const abaPedida: Aba = ABAS_CONHECIDAS.includes(subPedida as Aba)
    ? (subPedida as Aba)
    : 'veiculos';
  const abaAtual: Aba = abas.some((aba) => aba.valor === abaPedida) ? abaPedida : 'veiculos';

  const mudarAba = (valor: string) => {
    const proximos = new URLSearchParams(params);
    proximos.set('sub', valor);
    setParams(proximos, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota & Equipe"
        description="Gerencie em um único lugar os veículos, condutores, monitores e acessos do seu credenciado."
      />

      <Tabs value={abaAtual} onValueChange={mudarAba}>
        <TabsList className="h-auto w-full flex-wrap justify-start">
          {abas.map((aba) => (
            <TabsTrigger key={aba.valor} value={aba.valor} className="gap-2">
              <aba.icone className="h-4 w-4" aria-hidden="true" />
              {aba.rotulo}
            </TabsTrigger>
          ))}
        </TabsList>

        <Suspense fallback={<CardSkeleton />}>
          <TabsContent value="veiculos" className="mt-6">
            {abaAtual === 'veiculos' && <VeiculosPage embutida />}
          </TabsContent>
          {isColetivo && (
            <TabsContent value="condutores" className="mt-6">
              {abaAtual === 'condutores' && <CondutoresPage embutida />}
            </TabsContent>
          )}
          <TabsContent value="monitores" className="mt-6">
            {abaAtual === 'monitores' && <MonitoresPage embutida />}
          </TabsContent>
          {podeVerAcessos && (
            <TabsContent value="acessos" className="mt-6">
              {abaAtual === 'acessos' && <EquipePage embutida />}
            </TabsContent>
          )}
        </Suspense>
      </Tabs>
    </div>
  );
}

export default FrotaEquipePage;
